import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, BarChart3, Clock, Wallet, PiggyBank } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/lib/utils';

/**
 * Compute Net Cash Flow insights from monthlyIncomeExpenses
 */
function computeNetCashFlowInsight(monthlyIncomeExpenses) {
  if (!monthlyIncomeExpenses || monthlyIncomeExpenses.length === 0) {
    return { currentNet: null, trend: 'flat', label: 'No data' };
  }
  const latest = monthlyIncomeExpenses[monthlyIncomeExpenses.length - 1];
  const prev = monthlyIncomeExpenses.length > 1 
    ? monthlyIncomeExpenses[monthlyIncomeExpenses.length - 2] 
    : null;
  
  let trend = 'flat';
  if (prev && latest) {
    const diff = latest.net - prev.net;
    if (Math.abs(diff) > 0.01) {
      trend = diff > 0 ? 'improving' : 'declining';
    }
  }
  return { currentNet: latest.net, trend, label: latest.label };
}

/**
 * Compute Savings Rate insight from monthlyIncomeExpenses
 */
function computeSavingsRateInsight(monthlyIncomeExpenses) {
  if (!monthlyIncomeExpenses || monthlyIncomeExpenses.length === 0) {
    return { currentRate: null, avgRate: null, indicator: 'weak', label: 'No data' };
  }
  const latest = monthlyIncomeExpenses[monthlyIncomeExpenses.length - 1];
  const validRates = monthlyIncomeExpenses
    .map(m => m.savingsRate)
    .filter(r => r !== null && !isNaN(r));
  
  const avgRate = validRates.length > 0
    ? Number((validRates.reduce((a, b) => a + b, 0) / validRates.length).toFixed(1))
    : null;
  
  let indicator = 'weak';
  if (latest.savingsRate !== null) {
    if (latest.savingsRate >= 20) indicator = 'healthy';
    else if (latest.savingsRate >= 10) indicator = 'okay';
  }
  
  return { 
    currentRate: latest.savingsRate, 
    avgRate, 
    indicator, 
    label: latest.label 
  };
}

export default function SpendingAnalyticsPanel({ analytics }) {
  const { currency: userCurrency } = useAuth();
  const currency = userCurrency || 'USD';

  const { weeklyTotals, monthlyTotals, averageWeeklySpend, averageMonthlySpend, monthlyIncomeExpenses } = analytics || {};

  const netInsight = useMemo(() => computeNetCashFlowInsight(monthlyIncomeExpenses), [monthlyIncomeExpenses]);
  const savingsInsight = useMemo(() => computeSavingsRateInsight(monthlyIncomeExpenses), [monthlyIncomeExpenses]);
  
  if (!analytics) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.6 }}
      className="w-full overflow-hidden"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-[#118AB2] font-semibold">Historical analytics</p>
          <h2 className="text-lg sm:text-xl font-bold text-foreground">Spending insights at a glance</h2>
        </div>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1"><Clock size={16} /> Last 6 months</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Net Cash Flow Card */}
        <div className="p-4 bg-gray-50/80 dark:bg-slate-700/80 rounded-2xl">
          <div className="flex items-center gap-2 mb-2">
            <Wallet size={18} className="text-[#118AB2]" />
            <p className="text-xs font-semibold text-muted-foreground">Net Cash Flow</p>
          </div>
          {netInsight.currentNet !== null ? (
            <>
              <p className={`text-2xl font-bold ${netInsight.currentNet >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                {netInsight.currentNet >= 0 ? '+' : ''}{formatCurrency(netInsight.currentNet, currency)}
              </p>
              <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                {netInsight.trend === 'improving' && <TrendingUp size={12} className="text-emerald-500" />}
                {netInsight.trend === 'declining' && <TrendingDown size={12} className="text-rose-500" />}
                <span>
                  {netInsight.trend === 'improving' && 'Improving vs last month'}
                  {netInsight.trend === 'declining' && 'Declined vs last month'}
                  {netInsight.trend === 'flat' && 'Stable vs last month'}
                </span>
              </div>
            </>
          ) : (
            <p className="text-lg text-muted-foreground">No data yet</p>
          )}
        </div>

        {/* Savings Rate Card */}
        <div className="p-4 bg-gray-50/80 dark:bg-slate-700/80 rounded-2xl">
          <div className="flex items-center gap-2 mb-2">
            <PiggyBank size={18} className="text-[#06D6A0]" />
            <p className="text-xs font-semibold text-muted-foreground">Savings Rate</p>
          </div>
          {savingsInsight.currentRate !== null ? (
            <>
              <p className="text-2xl font-bold text-foreground">
                {savingsInsight.currentRate.toFixed(1)}%
              </p>
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                  savingsInsight.indicator === 'healthy' 
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400' 
                    : savingsInsight.indicator === 'okay'
                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400'
                    : 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-400'
                }`}>
                  {savingsInsight.indicator === 'healthy' && '≥20% – Great!'}
                  {savingsInsight.indicator === 'okay' && '10-20% – Okay'}
                  {savingsInsight.indicator === 'weak' && '<10% – Needs work'}
                </span>
                {savingsInsight.avgRate !== null && (
                  <span>Avg: {savingsInsight.avgRate.toFixed(1)}%</span>
                )}
              </div>
            </>
          ) : (
            <p className="text-lg text-muted-foreground">No income data</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-gray-50/80 dark:bg-slate-700/80 rounded-2xl">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={18} className="text-[#06D6A0]" />
            <p className="text-xs font-semibold text-muted-foreground">Average weekly spend</p>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(averageWeeklySpend || 0, currency)}</p>
          {weeklyTotals?.length > 0 && (
            <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
              {weeklyTotals.map((week) => (
                <li key={week.label} className="flex justify-between">
                  <span>{week.label}</span>
                  <span className="font-medium text-foreground">{formatCurrency(week.total, currency)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-4 bg-gray-50/80 dark:bg-slate-700/80 rounded-2xl">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 size={18} className="text-[#FFB703]" />
            <p className="text-xs font-semibold text-muted-foreground">Average monthly spend</p>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(averageMonthlySpend || 0, currency)}</p>
          {monthlyTotals?.length > 0 && (
            <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
              {monthlyTotals.map((month) => (
                <li key={month.label} className="flex justify-between">
                  <span>{month.label}</span>
                  <span className="font-medium text-foreground">{formatCurrency(month.total, currency)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </motion.div>
  );
}
